import React, { useRef, useState } from "react";
import {
    useLoaderData,
    useSubmit,
    useNavigation,
    useNavigate,
    useBlocker,
} from "react-router";
import axios from "axios";
import { Form, FloatingLabel, Button, Row, Col, Image } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import styled from "styled-components";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faCircleUp } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "components/shared/ThemeProvider";
import ConfirmCancelDialog from "components/shared/ConfirmDialog";
import {
    renderSavingProgressToast,
    renderLoadingProgressToast,
} from "components/shared/Toasts";
import { BasicInfoSchema } from "schema/account";
import McpTokenSection from "components/account/McpTokenSection";
import imagePlaceHolder from "assets/images/user_portrait_placeholder.png";

const ProfileImage = styled(Image)`
    margin: 1rem 0;
    width: 5rem;
`;

export async function loader() {
    const profile = await renderLoadingProgressToast(
        axios.get("account/profile")
    ).then((response) => response.data);
    const mcp_tokens = await axios
        .get("account/mcp-tokens")
        .then((response) => response.data)
        .catch(() => []);

    return { ...profile, mcp_tokens };
}

export async function action({ request }) {
    const updates = await request.json();
    if (updates["action"] === "basic_info") {
        const basic_info = await renderSavingProgressToast(
            axios
                .put(
                    "account/basic_info",
                    BasicInfoSchema.cast(updates["payload"])
                )
                .then((response) => response.data)
        );
        return basic_info;
    } else if (updates["action"] === "activate_sub") {
        await renderSavingProgressToast(axios.post("subscription/activate"));
    } else if (updates["action"] === "suspend_sub") {
        await renderSavingProgressToast(axios.post("subscription/suspend"));
    } else if (updates["action"] === "activate_acc") {
        await renderSavingProgressToast(axios.post("account/profile/activate"));
    } else if (updates["action"] === "suspend_acc") {
        await renderSavingProgressToast(axios.post("account/profile/suspend"));
    } else if (updates["action"] === "create_mcp_token") {
        // return failures as data so the section can show them inline instead of hitting the error boundary
        try {
            const response = await renderSavingProgressToast(
                axios.post("account/mcp-token", { name: updates["name"] })
            );
            return { token: response.data.token };
        } catch (e) {
            return {
                error:
                    e.response?.data?.description ||
                    "Failed to generate token.",
            };
        }
    } else if (updates["action"] === "revoke_mcp_token") {
        try {
            await renderSavingProgressToast(
                axios.delete(`account/mcp-token/${updates["id"]}`)
            );
        } catch {
            // the toast already reports the failure
        }
    }
    return null;
}

const UserProfile = () => {
    const { basic_info, subscription, mcp_tokens } = useLoaderData();
    const { isDarkMode } = useTheme();
    const [showSuspendConfirmDialog, setShowSuspendConfirmDialog] =
        useState(false);
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
        useState(false);
    const nav = useNavigate();
    const navigation = useNavigation();
    const isNavigating =
        navigation.state === "submitting" || navigation.state === "loading";
    const submit = useSubmit();
    const isSubmittingRef = useRef(false);

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isDirty },
    } = useForm({
        resolver: yupResolver(BasicInfoSchema),
        defaultValues: basic_info, // Use initial request, not changing record
    });

    const onSubmitBasicInfo = async (data) => {
        isSubmittingRef.current = true;
        try {
            await submit(
                { action: "basic_info", payload: data },
                { method: "put", encType: "application/json" }
            );
        } catch (error) {
            isSubmittingRef.current = false;
            throw error;
        }
    };

    const onActivateSubscription = async () => {
        await submit(
            { action: "activate_sub" },
            { method: "post", encType: "application/json" }
        );
    };

    const onSuspendSubscription = async () => {
        await submit(
            { action: "suspend_sub" },
            { method: "post", encType: "application/json" }
        );
    };

    const onActivateAccount = async () => {
        await submit(
            { action: "activate_acc" },
            { method: "post", encType: "application/json" }
        );
    };

    const onSuspendAccount = async () => {
        await submit(
            { action: "suspend_acc" },
            { method: "post", encType: "application/json" }
        );
    };

    const callbackConfirmDialog = (target) => async (event) => {
        if (target === "subscription") {
            setShowSuspendConfirmDialog(false);
            if (event === "confirm") {
                await onSuspendSubscription();
            }
        } else if (target === "account") {
            setShowDeleteConfirmDialog(false);
            if (event === "confirm") {
                await onSuspendAccount();
            }
        }
    };

    useBlocker(() => {
        // Don't block navigation if form is being submitted or already navigating
        if (!isDirty || isNavigating || isSubmittingRef.current) {
            return false;
        }

        if (
            window.confirm(
                "You have unsaved changes. Are you sure you want to leave?"
            )
        ) {
            return false; // Allow navigation
        } else {
            return true; // Block navigation
        }
    });

    const onError = (errors, e) => console.error(errors, e);

    return (
        <div className="my-3">
            <h5>Basic Info</h5>
            <hr />
            <Form
                onSubmit={handleSubmit(onSubmitBasicInfo, onError)}
                onReset={reset}
            >
                <Form.Group className="mb-3">
                    <ProfileImage
                        data-testid="img_profile_image"
                        src={
                            basic_info.picture_url
                                ? basic_info.picture_url
                                : imagePlaceHolder
                        }
                        referrerPolicy="no-referrer"
                        roundedCircle
                    />
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12" md="8">
                    <Controller
                        control={control}
                        name="name"
                        render={({ field: { onChange, value, ref } }) => (
                            <FloatingLabel
                                controlId="name"
                                label="Display Name"
                            >
                                <Form.Control
                                    type="text"
                                    rules={{ required: true }}
                                    isInvalid={errors.name}
                                    onChange={onChange}
                                    value={value}
                                    data-testid="input_name"
                                    ref={ref}
                                    placeholder="Display Name"
                                    name="name"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name?.message}
                                </Form.Control.Feedback>
                            </FloatingLabel>
                        )}
                    />
                </Form.Group>
                <Col className="mb-3" sm="12" md="8">
                    <Row>
                        <Form.Group as={Col} sm="6">
                            <Controller
                                control={control}
                                name="given_name"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="given_name"
                                        label="Given Name"
                                    >
                                        <Form.Control
                                            type="text"
                                            rules={{ required: true }}
                                            isInvalid={errors.given_name}
                                            onChange={onChange}
                                            value={value}
                                            data-testid="input_given_name"
                                            ref={ref}
                                            placeholder="Given Name"
                                            name="given_name"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.given_name?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                        <Form.Group as={Col} sm="6">
                            <Controller
                                control={control}
                                name="family_name"
                                render={({
                                    field: { onChange, value, ref },
                                }) => (
                                    <FloatingLabel
                                        controlId="family_name"
                                        label="Family Name"
                                    >
                                        <Form.Control
                                            type="text"
                                            rules={{ required: true }}
                                            isInvalid={errors.family_name}
                                            onChange={onChange}
                                            value={value}
                                            data-testid="input_family_name"
                                            ref={ref}
                                            placeholder="Family Name"
                                            name="family_name"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.family_name?.message}
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                )}
                            />
                        </Form.Group>
                    </Row>
                </Col>
                <Form.Group className="mb-3" as={Col} sm="12" md="8">
                    <FloatingLabel label="Email Address">
                        <Form.Control
                            plaintext
                            readOnly
                            defaultValue={basic_info.email}
                            data-testid="input_email"
                            style={{
                                color: isDarkMode ? "white" : "black",
                                backgroundColor: "transparent",
                            }}
                        />
                    </FloatingLabel>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12" md="8">
                    <FloatingLabel label="Identity Provider">
                        <Form.Control
                            plaintext
                            readOnly
                            defaultValue={basic_info.id_provider}
                            data-testid="input_id_provider"
                            style={{
                                color: isDarkMode ? "white" : "black",
                                backgroundColor: "transparent",
                            }}
                        />
                    </FloatingLabel>
                </Form.Group>
                <Row className="mb-3">
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Status">
                            <Form.Control
                                plaintext
                                readOnly
                                value={basic_info.status}
                                data-testid="input_status"
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Status Update Time">
                            <Form.Control
                                plaintext
                                readOnly
                                value={new Date(
                                    basic_info.status_update_time
                                ).toLocaleString()}
                                data-testid="input_status_update_time"
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                </Row>
                <Form.Group
                    className="mb-3"
                    hidden={basic_info.status === "Cancelled"}
                >
                    <Button
                        variant="success"
                        type="submit"
                        title="Save"
                        disabled={isNavigating}
                    >
                        <FontAwesomeIcon icon={faFloppyDisk} /> Save
                    </Button>
                </Form.Group>
            </Form>
            {basic_info.id_provider === "Wishliz" && (
                <>
                    <h5>Security</h5>
                    <hr />
                    <Form.Group className="mb-3">
                        <Button
                            variant="primary"
                            onClick={() => nav("/account/change-password")}
                            disabled={isNavigating}
                        >
                            Change Password
                        </Button>
                    </Form.Group>
                </>
            )}
            <McpTokenSection tokens={mcp_tokens} />
            <h5>Subscription</h5>
            <hr />
            <Form>
                <Form.Group className="mb-3" as={Col} sm="12" md="8">
                    <FloatingLabel label="Subscription Plan">
                        <Form.Control
                            plaintext
                            readOnly
                            value={subscription.plan_type_name}
                            data-testid="input_plan_type_name"
                            style={{
                                color: isDarkMode ? "white" : "black",
                                backgroundColor: "transparent",
                            }}
                        />
                    </FloatingLabel>
                </Form.Group>
                <Row
                    className="mb-3"
                    data-testid="row_upgrade_sub_plan"
                    hidden={
                        basic_info.status === "Cancelled" ||
                        subscription.status === "Suspended" ||
                        subscription.status === "Cancelled"
                    }
                >
                    <Form.Group className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            type="button"
                            title="Upgrade"
                            onClick={() => nav("/subscription/pricing/upgrade")}
                            disabled={isNavigating}
                        >
                            <FontAwesomeIcon icon={faCircleUp} /> Upgrade
                        </Button>
                        <Form.Text>Ugrade to other subscription plan</Form.Text>
                    </Form.Group>
                </Row>
                <Row
                    className="mb-3"
                    hidden={!subscription.status}
                    data-testid="row_sub_status"
                >
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Status">
                            <Form.Control
                                plaintext
                                readOnly
                                value={subscription.status}
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Last Update">
                            <Form.Control
                                plaintext
                                readOnly
                                value={new Date(
                                    subscription.status_update_datetime
                                ).toLocaleString()}
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                </Row>
                <Row
                    className="mb-3"
                    hidden={
                        basic_info.status === "Cancelled" ||
                        subscription.status === "Suspended" ||
                        subscription.status === "Cancelled" ||
                        subscription.payment_gateway_sub_id === null
                    }
                    data-testid="row_cancel_sub"
                >
                    <Form.Group className="mb-3 d-flex align-items-center gap-3">
                        <Button
                            variant="danger"
                            type="button"
                            title="Cancel"
                            onClick={() => setShowSuspendConfirmDialog(true)}
                            disabled={isNavigating}
                        >
                            {" "}
                            Cancel
                        </Button>
                        <Form.Text>Cancel Subscription</Form.Text>
                    </Form.Group>
                </Row>
                <Row
                    className="mb-3"
                    hidden={
                        basic_info.status === "Cancelled" ||
                        subscription.status === "Active" ||
                        subscription.status === "Cancelled" ||
                        subscription.payment_gateway_sub_id === null
                    }
                    data-testid="row_activate_sub"
                >
                    <Form.Group className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            type="button"
                            title="Activate"
                            onClick={onActivateSubscription}
                            disabled={isNavigating}
                        >
                            Activate
                        </Button>
                        <Form.Text>Activate Subscription</Form.Text>
                    </Form.Group>
                </Row>
                <Row
                    className="mb-3"
                    hidden={!subscription.payment_gateway}
                    data-testid="row_payment_gateway"
                >
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Payment Gateway">
                            <Form.Control
                                plaintext
                                readOnly
                                value={subscription.payment_gateway}
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} sm="6" md="4">
                        <FloatingLabel label="Payment Gateway Subscription Id">
                            <Form.Control
                                plaintext
                                readOnly
                                value={subscription.payment_gateway_sub_id}
                                style={{
                                    color: isDarkMode ? "white" : "black",
                                    backgroundColor: "transparent",
                                }}
                            />
                        </FloatingLabel>
                    </Form.Group>
                </Row>
            </Form>
            <h5>Delete Account</h5>
            <hr />
            <Form onSubmit={handleSubmit(onSuspendAccount, onError)}>
                <Row
                    className="mb-3"
                    hidden={basic_info.status === "Cancelled"}
                    data-testid="row_suspend_account"
                >
                    <Form.Group className="mb-3 d-flex align-items-center gap-3">
                        <Button
                            variant="danger"
                            type="button"
                            title="Delete"
                            onClick={() => setShowDeleteConfirmDialog(true)}
                            disabled={isNavigating}
                        >
                            {" "}
                            Delete
                        </Button>
                        <Form.Text>Delete Account</Form.Text>
                    </Form.Group>
                </Row>

                <Row
                    className="mb-3"
                    hidden={basic_info.status !== "Cancelled"}
                    data-testid="row_activate_account"
                >
                    <Form.Group className="mb-3 d-flex align-items-center gap-3">
                        <Button
                            variant="success"
                            type="button"
                            title="Undelete"
                            onClick={onActivateAccount}
                            disabled={isNavigating}
                        >
                            {" "}
                            Undelete
                        </Button>
                        <Form.Text>Undelete Account</Form.Text>
                    </Form.Group>
                </Row>
            </Form>
            <ConfirmCancelDialog
                data-testid="dialog-confirm-suspend"
                show={showSuspendConfirmDialog}
                title="Confirm Subscription Suspension"
                callback={callbackConfirmDialog("subscription")}
            >
                <div>
                    <p>
                        Are you sure you want to suspend your subscription? This
                        will have the following effects:
                    </p>
                    <ul className="mb-3">
                        <li>Your account will become read-only</li>
                        <li>Your wishlist items will be reserved</li>
                        <li>You will no longer receive any product events</li>
                        <li>
                            You can reactivate your subscription within 14 days
                            (deadline:{" "}
                            {new Date(
                                Date.now() + 14 * 24 * 60 * 60 * 1000
                            ).toLocaleDateString()}
                            )
                        </li>
                        <li>
                            After 14 days, your subscription will be permanently
                            cancelled
                        </li>
                    </ul>
                </div>
            </ConfirmCancelDialog>
            <ConfirmCancelDialog
                data-testid="dialog-confirm-delete"
                show={showDeleteConfirmDialog}
                title="Confirm Delete Account"
                callback={callbackConfirmDialog("account")}
            >
                <div>
                    <p>
                        Are you sure you want to delete your account? This will
                        have the following effects:
                    </p>
                    <ul className="mb-3">
                        <li>Your account will become read-only</li>
                        <li>
                            Your wishlist items will be reserved for 14 days
                        </li>
                        <li>
                            Your current subscription will be suspended for 14
                            days
                        </li>
                        <li>You will no longer receive any product events</li>
                        <li>
                            After 14 days, your account, wishlist items, and
                            subscription will be permanently deleted (deadline:{" "}
                            {new Date(
                                Date.now() + 14 * 24 * 60 * 60 * 1000
                            ).toLocaleDateString()}
                            ). <strong>This is irreversible.</strong>
                        </li>
                    </ul>
                </div>
            </ConfirmCancelDialog>
        </div>
    );
};

export default UserProfile;
